import type { RowData } from "@tanstack/react-table";
import { z } from "zod";

declare module "@tanstack/react-table" {
  interface ColumnMeta<TData extends RowData, TValue> {
    align?: "left" | "center" | "right";
    /**
     * Under `layout="fixed"`, reads this column's `size` as PIXELS instead of as a
     * weight; those pixels come off the table before the rest divide the remainder.
     * For a row-actions column. Ignored under `auto`.
     */
    fixedWidth?: boolean;
    /**
     * The narrowest this column may render, in pixels, under `layout="fixed"`;
     * together the minimums are where the table stops shrinking and the wrapper
     * scrolls. Defaults to what the heading needs (`label` + sort control +
     * padding); set it to widen a column whose values need more, or to narrow one
     * whose label overstates it.
     */
    minWidth?: number;
    /**
     * Marks the column server-sortable, so its `<th>` exposes `aria-sort`. Not
     * TanStack's `enableSorting`: `getCanSort()` ANDs it with the table flag, which
     * is `false` here because sorting is server-driven.
     */
    sortable?: boolean;
    /**
     * The column name as a STRING, for the view-options list and the default
     * `minWidth`. A `header` may be JSX, so it cannot serve as one.
     */
    label?: string;
  }
}

/**
 * The `{columnVisibility, columnOrder}` pair persisted per table (design D5,
 * adr/0007). Validated on read, so a stale shape from an earlier release falls
 * back to the consumer's defaults instead of reaching `useReactTable`.
 */
const ViewOptionsPreferenceSchema = z.object({
  columnVisibility: z.record(z.string(), z.boolean()),
  columnOrder: z.array(z.string()),
});

type ViewOptionsPreference = z.infer<typeof ViewOptionsPreferenceSchema>;

const SortOrderSchema = z.enum(["asc", "desc"]);

type SortOrder = z.infer<typeof SortOrderSchema>;

export { SortOrderSchema, ViewOptionsPreferenceSchema };
export type { SortOrder, ViewOptionsPreference };
